import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { QuantitativePanel } from './QuantitativePanel';
import { QualitativePanel } from './QualitativePanel';
import { SignalsPanel } from './SignalsPanel';
import { insightPeriod, kpis } from '../../data/snakInsights';
type Tab = 'quant' | 'qual' | 'signals';
const TABS: {
  id: Tab;
  label: string;
  sub: string;
}[] = [{
  id: 'quant',
  label: 'Quantitative',
  sub: 'What they did'
}, {
  id: 'qual',
  label: 'Qualitative',
  sub: 'What they told us'
}, {
  id: 'signals',
  label: 'Signals',
  sub: 'What it means'
}];
type InsightsOverlayProps = {
  open: boolean;
  onClose: () => void;
};

/** Full-screen illustrative reporting view for the Caprelsa Snak. */
export function InsightsOverlay({
  open,
  onClose
}: InsightsOverlayProps) {
  const [tab, setTab] = useState<Tab>('quant');
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    setTab('quant');
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return createPortal(<AnimatePresence data-id="element-50" data-mp="3el3158layfgt">
      {open && <motion.div role="dialog" aria-modal="true" aria-labelledby="insights-title" initial={{
      opacity: 0,
      scale: 0.985
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.985
    }} transition={{
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }} className="fixed inset-0 z-[90] flex flex-col bg-[var(--surface)] hide-on-print" data-id="element-51" data-mp="g24e2y8e0e76">
          <header className="flex-shrink-0 border-b border-[var(--fg)]/[0.07] bg-white px-[clamp(1.25rem,3vw,3rem)] pt-[clamp(1rem,1.8vw,1.5rem)]" data-id="element-52" data-mp="2qup4t5vopugf">
            <div className="flex items-start justify-between gap-6" data-id="element-53" data-mp="2ltro6xqbp8h1">
              <div className="min-w-0" data-id="element-54" data-mp="1dwoyc16vpz31">
                <div className="flex flex-wrap items-center gap-2" data-id="element-55" data-mp="1dtj4k8nb290z">
                  <p className="font-mono text-[0.66rem] uppercase tracking-[0.16em] text-[var(--brand)]" data-id="element-56" data-mp="1hvxs361x5wyl">Caprelsa Snak · Insight report</p>
                  <span className="rounded-full border border-dashed border-[var(--brand)]/60 px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-[var(--brand)]" data-id="element-57" data-mp="13zu25oy2warx">
                    Illustrative data · not campaign results
                  </span>
                </div>
                <h2 id="insights-title" className="mt-1.5 text-[clamp(1.4rem,2.6vw,2.4rem)] font-extrabold leading-tight tracking-[-0.035em] text-[var(--fg)]" data-id="element-58" data-mp="z0he50vh0xnr">
                  What matters to the HCP — in numbers and in their words.
                </h2>
                <p className="mt-1 text-[clamp(0.72rem,0.9vw,0.85rem)] text-[var(--muted)]" data-id="element-59" data-mp="b9tqn792nzg3">{insightPeriod}</p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Close insight report" className="flex h-9 flex-shrink-0 items-center gap-1.5 rounded-full bg-[var(--fg)] px-3.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-60" data-mp="g73nh7ysjddy">
                <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-61" data-mp="fk6011bhw6ix" />
                Close
              </button>
            </div>

            <dl className="mt-[clamp(0.9rem,1.6vw,1.3rem)] grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5" data-id="element-62" data-mp="biugndj7shj6">
              {kpis.map((k, i) => <motion.div key={k.label} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1 + i * 0.05,
            duration: 0.4
          }} className="border-l-2 border-[var(--brand)] pl-3" data-id="element-63" data-mp="kbe9fxctrrw3" data-mpi={i}>
                  <dd className="text-[clamp(1.3rem,2.2vw,2rem)] font-extrabold leading-none tracking-[-0.04em] text-[var(--fg)] tabular-nums" data-id="element-64" data-mp="2nrt1qke7jgzl">{k.value}</dd>
                  <dt className="mt-1 text-[clamp(0.7rem,0.85vw,0.8rem)] font-semibold text-[var(--fg)]" data-id="element-65" data-mp="2xie0c7tvslii">{k.label}</dt>
                  <p className="text-[0.66rem] text-[var(--muted)]" data-id="element-66" data-mp="32i0dkh9pm1rb">{k.detail}</p>
                </motion.div>)}
            </dl>

            <div role="tablist" aria-label="Report views" className="mt-[clamp(0.9rem,1.6vw,1.3rem)] flex gap-6" data-id="element-67" data-mp="24vjfvax2dm51">
              {TABS.map(t => <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} className="relative pb-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]" data-id="element-68" data-mp="2erd1rkwoarzh">
                  <span className={`block text-[clamp(0.82rem,1vw,0.95rem)] font-bold ${tab === t.id ? 'text-[var(--fg)]' : 'text-[var(--muted)] hover:text-[var(--fg)]'}`} data-id="element-69" data-mp="3v4bczolkxwmj">{t.label}</span>
                  <span className="block text-[0.66rem] text-[var(--muted)]" data-id="element-70" data-mp="1ek7tqhf1erb0">{t.sub}</span>
                  {tab === t.id && <motion.span layoutId="insights-tab" className="absolute inset-x-0 -bottom-px h-0.5 bg-[var(--brand)]" data-id="element-71" data-mp="19r53ai5k3xz1" />}
                </button>)}
            </div>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-[clamp(1.25rem,3vw,3rem)] py-[clamp(1rem,1.8vw,1.5rem)]" data-id="element-72" data-mp="zw4ml4p9d9km">
            {tab === 'quant' && <QuantitativePanel data-id="element-73" data-mp="qtvj2yseqggq" />}
            {tab === 'qual' && <QualitativePanel data-id="element-74" data-mp="qudlye9krsxm" />}
            {tab === 'signals' && <SignalsPanel data-id="element-75" data-mp="lvk98o2445yi" />}
          </div>
        </motion.div>}
    </AnimatePresence>, document.body);
}